import { generateSetChild, generateAddArrayChild, generateUpdateArrayChild, generateRemoveArrayChild } from '@/helpers/VuexHelpers'
import { service } from '@/service'
import cookie from 'cookie_js'
import { config } from '@/config'
import { format } from '@/utils/format'
import { dayjs } from '@/helpers/dayjs'

let cartPromise = null

export default {
	namespaced: true,
	state: {
		token: "",
		qrtoken: "",
		userDetail: {
			firstName: "",
			lastName: "",
			email: "",
			gender: "",
			subscribedToNewsletter: "",
			phoneNumber: '',
		},
		referralToken: '',
		referralCount: 0,
		coupons: [],
		addresses: [],
		lastOrders: [],
		favoriteProducts: [],
		club: {},
		claimableCoupons: [],
	},
	actions: {
		async getUser({commit, dispatch, state}, { accumulatorGroup = null } = {} ) {
			if (!state.token) {
				let token = cookie.get(config.LOGIN_COOKIE)
				if (token) {
					commit('setToken', {item: token})
					// HACK: we set a default header for the bearer
					service.defaultHeaders.Authorization = `Bearer ${token}`
				}
			}
			if (!state.token) {
				//throw new Error("no token, login first")
				return Promise.reject(new Error("no token, login first"))
			}
			try {
				let result = await service.me({ brand: config.BRAND, token: state.token, accumulatorGroup })
				dispatch('readUser', result);
			} catch(err) {
				// do a logout to clear all data
				dispatch('logout')
			}
			return false
		},
		readUser({commit}, user) {
			const userDetail = {
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
				gender: user.gender,
				subscribedToNewsletter: user.subscribedToNewsletter,
				groups: user.groups,
				phoneNumber: user.phoneNumber,
				birthday: user.birthday ? dayjs(user.birthday).format('L') : user.birthday,
			}
			const coupons = user.promotionCoupons
				.map(v => ({
					code: v.code,
					discount: v.discount,
					name: v.name,
					validUntil: v.validUntil,
				}))

			const club = {
				points: user.club.balance,
				pointsTotal: user.club.lifeTimePoints,
				pointsToNext: Math.max(user.club.nextTier.start - user.club.balance, 0),
				tierLevel: user.club.tier.label,
				nextTierLevel: user.club.nextTier.label,
			};

			const claimableCoupons = user.claimableCoupons
				.map(v => ({
					code: v.code,
					amount: v.amount,
					label: v.label,
					image: v.image,
					points: v.points,
					eligible: v.points <= club.points,
				}));

			commit('setUserDetail', { item: userDetail })
			commit('setCoupons', { item: coupons })
			commit('setClaimableCoupons', { item: claimableCoupons })
			commit('setClub', { item: club })
			commit('setReferralToken', { item: user.referralToken })
			commit('setReferralCount', { item: user.referralCount || user.club.referralCount })
		},
		async updateUser ({state, dispatch}, {userDetail, accumulatorGroup = null } = {}) {
			const result = await service.accountUpdateMe({userDetail, token: state.token, brand: config.BRAND, accumulatorGroup});
			dispatch('readUser', result);
		},
		async login({commit, dispatch, state}, {email, password, brand}) {
			try {
				let result = await service.login({email, password, brand})
				commit('setToken', { item: result.token })
				// HACK: Previously set() resulted in a path of /de. We need the path to be
				// the root page, because we need this cookie to access /shopmanager/restore-cart
				cookie.set(config.LOGIN_COOKIE, result.token, {path: '/'})
				// HACK: we set a default header for the bearer
				service.defaultHeaders.Authorization = `Bearer ${result.token}`
				return result;
			} catch(err) {
				throw err
			}
		},
		async logout({commit, dispatch, state}, { preserveStore = false } = { preserveStore: false }) {
			commit('setToken', { item: "" })
			delete service.defaultHeaders.Authorization
			cookie.remove(config.LOGIN_COOKIE)
			// HOTFIX: this is a fallback for unknown reason
			cookie.removeSpecific(config.LOGIN_COOKIE, { path: '/' })

			// HACK: we unset header for the bearer
			if (!preserveStore) {
				delete service.defaultHeaders.Authorization
				state.userDetail = {}
				state.token = ""
				state.qrtoken = ""
				state.addresses = []
				state.lastOrders = []
			}
		},
		async register({ commit, dispatch, state }, { email, password, birthday, firstName, lastName, phoneNumber, checkTerms, brand, userGroup, userCoupon, registerConfirmUrl, recaptchaResponse }) {
			try {
				return await service.accountRegister({ email, password, birthday, firstName, lastName, phoneNumber, checkTerms, brand, userGroup, userCoupon, registerConfirmUrl, recaptchaResponse })
			} catch(err) {
				throw err
			}
		},
		async loadLastOrders({commit, dispatch, state}, { channel = null, shippingCode = null, limit = 10, accumulatorGroup = null } = {} ) {
			try {
				let result = await service.accountOrders({ token: state.token, accumulatorGroup, channel, shippingCode, limit })
				let simpleLastOrders = result.reduce((p, c) => {
					if (true) {
						let order = {
							tokenValue: c.tokenValue,
							number: c.number,
							total: c.totals.total,
							channel: c.channel,
							channelName: c.channelName,
							shipmentCode: c.shipments[0]
								? c.shipments[0].method.code
								: "unknown",
							deliveryTime: c.deliveryTime,
							checkoutCompletedAt: c.checkoutCompletedAt,
							itemsLine: c.items
								.reduce((p, item) => {
									if (true) {
										//TODO: check for additional products
										p.push(`${item.quantity}x ${item.product.name}`)
									}
									return p
								}, [])
								.join(", ")
						}
						p.push(order)
					}

					return p
				}, [])

				commit('setLastOrders', {item: simpleLastOrders })

			} catch(err) {
				//TODO: we will handle this later
			}
		},
		async loadFavoriteProducts({ commit, dispatch, state, rootState }, { accumulatorGroup = null } = {}) {
			const channel = rootState.cart.serverData ? rootState.cart.serverData.channel.code : null
			const cartToken = rootState.cart.serverData ? rootState.cart.basketToken : null
			const result = await service.accountFavoriteProducts({ token: state.token, channel, cartToken, accumulatorGroup })

			let productList = result.items.reduce((p, c) => {
				// TODO: this is copied from `loadProductList` and therefor is redundant, should be centralized somewhere
				let item = {
					tags: c.attributes.reduce((p, c) => {
						if (c.code == "tags") {
							//TODO: we can remove this check in a future version
							if (!(c.value instanceof Array)) {
								throw new Error("productlist::readTags deprecated API")
							}
							p = p.concat(
								c.value.map(v => v["key"])
							)
						}
						return p
					}, []),
					// add default stock
					stock: -1,
					...c,

					variants: Object.entries(c.variants).reduce((p, [k, v]) => {
						//polyfill stock
						v.stock = v.stock == null ? -1 : v.stock
						p[k] = v
						return p
					}, {})
				}
				p.push(item)
				return p
			}, [])

			commit('setFavoriteProdcuts', { item: productList })
			return result
		},
		async requestPasswordReset({commit, dispatch, state}, { email }) {
			let result = await service.accountRequestPasswordReset({ email, passwordResetUrl: config.PASSWORD_RESET_URL, brand: config.BRAND })
			return result
		},
		async resetPassword({ state, getters }, { current, password, confirm, token }) {
			if (getters.isLoggedIn) {
				let result = await service.accountChangePassword({ current, password, confirm, token: state.token })
				return result
			}
			let result = await service.accountResetPassword({ password, confirm, token, brand: config.BRAND })
			return result
		},
		async loadAddresses({ commit, dispatch, state}, { accumulatorGroup = null } = {}) {
			// let result = await service.accountAddresses({ token: state.token })
			try {
				let result = await service.accountLastAddress({ token: state.token, brand: config.BRAND, accumulatorGroup })
				commit('setAddresses', { item: [result] })
			}
			catch(err) {
				// most likely there is no last address
			}
		},
		async updateAddress({ commit, dispatch, state}, { userAddress, accumulatorGroup = null } = {}) {
			try {
				userAddress.brand = config.BRAND;
				let result = await service.accountUpdateAddress({ userAddress, token: state.token, accumulatorGroup })
				commit('setAddresses', { item: [result] })
			}
			catch(err) {
				throw err;
			}
		},
		async claimCoupon({ commit, dispatch, state}, { code, accumulatorGroup = null } = {}) {
			try {
				let result = await service.accountClaimCoupon({ code, token: state.token, accumulatorGroup })
				dispatch('readUser', result);
			} catch(err) {
				throw err
			}
		},
		async loadQRToken({ commit, state }) {
			try {
				let result = await service.accountGetQRToken({ token: state.token })
				commit('setQRToken', { item: result.token })
			} catch (err) {
				throw err
			}
		},
		async abandonOptout({ commit, dispatch, state }, { token }) {
			try {
				return await service.abandonOptout({ token })
			} catch(err) {
				throw err
			}
		},
	},
	getters: {
		accountUserDetail(state) {
			return state.userDetail
				? state.userDetail
				: {}
		},
		isLoggedIn(state) {
			//TODO: currently simple check for username
			return state.userDetail && state.userDetail.email
		},
		lastOrders(state) {
			return state.lastOrders.map(order => {
				return {
					...order,
					url: `${config.SHOPMANAGER_ENDPOINT}/order-confirmation/${order.tokenValue}`
				}
			})
		},
		favoriteProducts(state) {
			return state.favoriteProducts
		},
		accountToken(state) {
			return state.token
		},
		accountLastAddress(state) {
			// return last address
			return state.addresses.slice(-1)[0]
		},
		accountGroups(state) {
			return state.userDetail.groups || [];
		},
		accountGroupCodes(state, getters) {
			return getters.accountGroups.map(group => group.code);
		},
		accountCoupons(state) {
			return state.coupons
		},
		accountClaimableCoupons(state) {
			return state.claimableCoupons
		},
		accountClub(state) {
			return state.club
		},
		accountQrToken(state) {
			return state.qrtoken
		},
		accountQrTokenPrefixed(state) {
			return config.ACCOUNT_QR_PREFIX + state.qrtoken
		},
		accountReferralUrl(state) {
			return state.referralToken
				? format(config.REFERRAL_URL, {token: state.referralToken})
				: null;
		},
		accountReferralCount(state) {
			return state.referralCount
		}
	},
	mutations: {
		setToken: generateSetChild("token"),
		setUserDetail: generateSetChild("userDetail"),
		setCoupons: generateSetChild("coupons"),
		setLastOrders: generateSetChild("lastOrders"),
		setFavoriteProdcuts: generateSetChild("favoriteProducts"),
		setAddresses: generateSetChild("addresses"),
		setReferralToken: generateSetChild("referralToken"),
		setReferralCount: generateSetChild("referralCount"),
		setQRToken: generateSetChild("qrtoken"),
		setClaimableCoupons: generateSetChild("claimableCoupons"),
		setClub: generateSetChild("club"),
	}
}
